import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import TextWithExternalLink from "../../../../components/ExternalLink";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { isUrlValid } from "../../../../utils/utils";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import navigate_to_api_keys from "./images/ninjaone_navigate_to_api_keys.png";

const NINJAONE_CODE_NAME = "ninjaone";

export default function NinjaOneIntegration() {
  const queryClient = useQueryClient();
  const colors = useColors();
  const [searchParams] = useSearchParams();
  const [apiKey, setApiKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [consoleUrl, setConsoleUrl] = useState("");
  const [showConsoleUrlError, setShowConsoleUrlError] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const { registerNinjaOneIntegration } = useAPI();
  const [registerIsLoading, setRegisterIsLoading] = useState(false);
  const { isIntegrated, isIntegratedLoading } =
    useIsIntegrated(NINJAONE_CODE_NAME);

  const guideMode = searchParams.has("viewonly");

  const onSubmit = async () => {
    if (!isUrlValid(consoleUrl) || !consoleUrl.includes("ninjarmm.com")) {
      setShowConsoleUrlError(true);
      return;
    }
    setRegisterIsLoading(true);
    try {
      await registerNinjaOneIntegration(apiKey, secretKey, consoleUrl);
      queryClient.invalidateQueries({
        queryKey: integrationKeys.all,
      });
      Toast.success("NinjaOne registered successfully");
    } catch (error) {
      if (error.response) {
        Toast.error(`Something went wrong: ${error.response.data.detail}`);
      } else {
        Toast.error("An error occurred while processing the request.");
      }
    } finally {
      setRegisterIsLoading(false);
    }
  };

  return (
    <Box>
      <Guideline
        title={isIntegrated ? "" : "NinjaOne integration instructions"}
        image={logos.NinjaOneLogo}
        iconBorderRadius={0}
        name={NINJAONE_CODE_NAME}
        content={
          isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>NinjaOne is registered!</Typography>
            </Box>
          ) : (
            <Box>
              <GuidelineStepByStep>
                <GuidelineStep title="Generate API Key">
                  <TextWithExternalLink
                    text="Open your"
                    linkText="NinjaOne console"
                    href="https://www.ninjaone.com/auth/"
                  />
                  <Typography variant="body2">
                    Navigate to: Administration {">"} Apps {">"} API {">"}{" "}
                    Legacy API Keys
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: navigate_to_api_keys,
                        altext: "navigate to legacy api",
                      },
                    ]}
                    onExpanded={() => {}}
                  />
                  <Typography variant="body2">
                    Click the "Generate API Key" button and create a new API
                    key.
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Enter the API Key">
                  <Box
                    width={700}
                    display="flex"
                    flexDirection="column"
                    gap={2}
                  >
                    <TextField
                      placeholder="Access key ID"
                      fullWidth
                      type="text"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      disabled={guideMode}
                    />
                    <TextField
                      placeholder="Secret access key"
                      fullWidth
                      type={showSecretKey ? "text" : "password"}
                      value={secretKey}
                      onChange={(e) => setSecretKey(e.target.value)}
                      disabled={guideMode}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle secret access key visibility"
                              onClick={() => setShowSecretKey((prev) => !prev)}
                            >
                              {showSecretKey ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </GuidelineStep>
                <GuidelineStep title="Enter your console URL">
                  <Box
                    width={700}
                    display="flex"
                    flexDirection="column"
                    gap={2}
                  >
                    <Typography variant="body2">
                      Copy the URL of your NinjaOne console {"(including "}{" "}
                      <code>https://</code>
                      {")"}.
                    </Typography>
                    <Box position="relative">
                      {showConsoleUrlError && (
                        <Typography
                          variant="caption"
                          color={colors.error_light_label_text}
                          position="absolute"
                          bottom={-25}
                          right={0}
                        >
                          invalid console URL
                        </Typography>
                      )}
                      <TextField
                        placeholder="Console URL"
                        fullWidth
                        type="text"
                        value={consoleUrl}
                        onChange={(e) => setConsoleUrl(e.target.value)}
                        disabled={guideMode}
                      />
                    </Box>
                    <ContainedButton
                      onClick={onSubmit}
                      size="small"
                      color="primary"
                      disabled={
                        guideMode || !apiKey || !secretKey || !consoleUrl
                      }
                      loading={registerIsLoading}
                    >
                      Connect
                    </ContainedButton>
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </Box>
          )
        }
      />
    </Box>
  );
}
